import {
  Button,
  Grid,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Box,
  Paper,
  Avatar,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PrinterConfigCard from "./PrinterConfigCard";
import { useEffect, useState } from "react";
import userService from "../../services/userService";
import warehouseService from "../../services/warehouseService";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import MiPlanCard from "./MiPlanCard";
import SecurityCard from "./SecurityCard";
import * as React from "react";
import {
  displayCompanyName,
  isCompanyPlaceholder,
  PROFILE_UPDATED_EVENT,
} from "../../utils/profileCompleteness";

const Perfil = () => {
  const [depositos, setDepositos] = useState([]);
  const [deposito, setDeposito] = useState(null);
  const [depositoOriginal, setDepositoOriginal] = useState(null);
  const [canEditUser, setCanEditUser] = useState(false);
  const [canEditDeposito, setCanEditDeposito] = useState(false);
  const [user, setUser] = useState(null);
  const [userOriginal, setUserOriginal] = useState();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [alertObj, setAlertObj] = useState({ severity: "success", message: "" });
  // Errores de validación por campo (todos los campos del perfil son obligatorios).
  const [userErrors, setUserErrors] = useState({});
  const [depositoErrors, setDepositoErrors] = useState({});
  const [scanOnPrepare, setScanOnPrepare] = useState(true);
  const [autoPrintOnConfirm, setAutoPrintOnConfirm] = useState(false);

  useEffect(() => {
    const uid = sessionStorage.getItem("User");
    if (uid) {
      warehouseService.getScanOnPrepare(uid).then(setScanOnPrepare);
      warehouseService.getAutoPrintOnConfirm(uid).then(setAutoPrintOnConfirm);
    }
  }, []);

  const handleAutoPrintToggle = async (e) => {
    const value = e.target.checked;
    setAutoPrintOnConfirm(value);
    try {
      await warehouseService.setAutoPrintOnConfirm(sessionStorage.getItem("User"), value);
      setAlertObj({
        severity: "success",
        message: value
          ? "Impresión automática de etiquetas activada."
          : "Impresión automática de etiquetas desactivada.",
      });
      setSnackbarOpen(true);
    } catch (err) {
      setAutoPrintOnConfirm(!value);
      setAlertObj({ severity: "error", message: "No se pudo guardar la preferencia." });
      setSnackbarOpen(true);
    }
  };

  const handleScanToggle = async (e) => {
    const value = e.target.checked;
    setScanOnPrepare(value);
    try {
      await warehouseService.setScanOnPrepare(sessionStorage.getItem("User"), value);
      setAlertObj({
        severity: "success",
        message: value
          ? "Preparación con escáner activada."
          : "Preparación sin escáner (marca manual) activada.",
      });
      setSnackbarOpen(true);
    } catch (err) {
      setScanOnPrepare(!value);
      setAlertObj({ severity: "error", message: "No se pudo guardar la preferencia." });
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    userService.getProfile().then((res) => {
      let data = res.data;
      // El super-admin (id=1) es owner por convención del sistema (mismo criterio
      // que el backend), aunque is_owner no venga seteado.
      const isSuperAdmin = Number(sessionStorage.getItem("User")) === 1;
      const userData = {
        id: Number(sessionStorage.getItem("User")),
        name: data.name || "",
        firstname: data.firstname || "",
        lastname: data.lastname || "",
        // El autogenerado "Nombre Apellido" (o "VORAS") se muestra VACÍO:
        // nadie lo cargó, y verlo lleno mientras el banner reclama Empresa
        // parecía un bug.
        company: displayCompanyName(data.company?.name, data.firstname, data.lastname),
        email: data.email || "",
        is_owner: Boolean(data.is_owner) || isSuperAdmin,
      };
      setUser(userData);
      setUserOriginal(userData);
    });
    fetchDepositos();
  }, []);

  const fetchDepositos = async () => {
    const result = await userService.getDeposits();
    setDepositos(result.data);
    if (result.data?.length > 0) {
      setDeposito(result.data[0]);
      setDepositoOriginal(result.data[0]);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const hasUserChanges = () => {
    if (!user || !userOriginal) return false;
    // Usuario y Email son la identidad del login (solo lectura): no se comparan.
    return (
      user.firstname !== userOriginal.firstname ||
      user.lastname !== userOriginal.lastname ||
      user.company !== userOriginal.company
    );
  };

  // Todos los campos del perfil son obligatorios: devuelve el mapa de errores
  // (vacío si está todo completo). Usa las mismas reglas que el banner global.
  const validateUser = () => {
    const errors = {};
    if (!user?.firstname?.trim()) errors.firstname = "Campo obligatorio";
    if (!user?.lastname?.trim()) errors.lastname = "Campo obligatorio";
    if (!user?.company?.trim()) {
      errors.company = "Campo obligatorio";
    } else if (isCompanyPlaceholder(user.company, user.firstname, user.lastname)) {
      errors.company = "Ingresá el nombre real de tu negocio";
    }
    return errors;
  };

  const validateDeposito = () => {
    const errors = {};
    if (!deposito?.name?.trim()) errors.name = "Campo obligatorio";
    if (!deposito?.street?.trim()) errors.street = "Campo obligatorio";
    if (!deposito?.number?.trim()) errors.number = "Campo obligatorio";
    if (!deposito?.reference?.trim()) errors.reference = "Campo obligatorio";
    if (!deposito?.postal_code?.trim()) errors.postal_code = "Campo obligatorio";
    if (!deposito?.province?.trim()) errors.province = "Campo obligatorio";
    if (!deposito?.department?.trim()) errors.department = "Campo obligatorio";
    if (!deposito?.phone?.trim()) errors.phone = "Campo obligatorio";
    return errors;
  };

  // Avisa al banner global de datos incompletos que re-evalúe.
  const notifyProfileUpdated = () => {
    window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
  };

  const hasDepositoChanges = () => {
    if (!deposito) return false;
    // Depósito nuevo (la empresa todavía no tiene ninguno): habilitar Guardar en
    // cuanto haya al menos un nombre cargado. Sin esto, con depositoOriginal null
    // el botón quedaba deshabilitado para siempre y no se podía crear el depósito.
    if (!depositoOriginal) return Boolean(deposito.name && deposito.name.trim());
    return (
      deposito.name !== depositoOriginal.name ||
      deposito.street !== depositoOriginal.street ||
      deposito.number !== depositoOriginal.number ||
      deposito.reference !== depositoOriginal.reference ||
      deposito.postal_code !== depositoOriginal.postal_code ||
      deposito.province !== depositoOriginal.province ||
      deposito.department !== depositoOriginal.department ||
      deposito.phone !== depositoOriginal.phone
    );
  };

  async function handleEditUser() {
    if (canEditUser) {
      const errors = validateUser();
      setUserErrors(errors);
      if (Object.keys(errors).length > 0) {
        setAlertObj({ severity: "error", message: "Completá los campos obligatorios" });
        setSnackbarOpen(true);
        return; // sigue en modo edición hasta completar
      }
      try {
        // Usuario/Email no viajan: son la identidad del login (solo lectura).
        // La empresa va como company.name: el backend renombra la company de
        // la membresía del owner (el company_id no cambia).
        const payload = {
          id: user.id,
          firstname: user.firstname.trim(),
          lastname: user.lastname.trim(),
          company: { name: user.company.trim() },
        };

        await userService.postUpdateUsuario(payload);
        setUserOriginal({ ...user });
        notifyProfileUpdated();
        setAlertObj({
          severity: "success",
          message: "Perfil actualizado con exito",
        });
      } catch (error) {
        console.error(error);
        setAlertObj({
          severity: "error",
          message: "Error al actualizar perfil",
        });
      } finally {
        setSnackbarOpen(true);
      }
    }
    setCanEditUser(!canEditUser);
  }

  async function handleEditDeposito() {
    if (canEditDeposito) {
      const errors = validateDeposito();
      setDepositoErrors(errors);
      if (Object.keys(errors).length > 0) {
        setAlertObj({ severity: "error", message: "Completá los campos obligatorios" });
        setSnackbarOpen(true);
        return; // sigue en modo edición hasta completar
      }
      try {
        // El backend deriva el company_id del user_id, así que lo incluimos siempre.
        const payload = { ...deposito, user_id: Number(sessionStorage.getItem("User")) };
        if (deposito?.id) {
          await warehouseService.updateWarehouse(payload);
        } else {
          // La empresa no tenía depósito: lo creamos.
          await warehouseService.createWarehouse(payload);
        }
        // Recargamos para traer el id (y demás campos) del depósito recién creado.
        await fetchDepositos();
        notifyProfileUpdated();
        setAlertObj({ severity: "success", message: "Deposito guardado con exito" });
      } catch (error) {
        console.error(error);
        setAlertObj({ severity: "error", message: "Error al guardar deposito" });
      } finally {
        setSnackbarOpen(true);
      }
      setCanEditDeposito(false);
      return;
    }
    // Entrando a modo edición sin depósito previo: inicializamos uno vacío para
    // que los campos sean editables y Guardar pueda habilitarse.
    if (!deposito) {
      setDeposito({
        name: "",
        street: "",
        number: "",
        reference: "",
        postal_code: "",
        province: "",
        department: "",
        phone: "",
      });
    }
    setCanEditDeposito(true);
  }

  const getInitials = () => {
    if (!user) return "?";
    const first = user.firstname?.charAt(0) || "";
    const last = user.lastname?.charAt(0) || "";
    return (first + last).toUpperCase() || user.name?.charAt(0)?.toUpperCase() || "?";
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
  };

  return (
    <>
      <Box sx={{ px: 5, py: 3 }}>
        {/* Page header */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "#1a1a1a",
            pl: 1.5,
            mb: 3,
            borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
            lineHeight: 1.5,
          }}
        >
          Mi cuenta
        </Typography>

        <Grid container spacing={3}>
          {/* Mi plan: plan vigente + consumo de egresos del ciclo */}
          <Grid item xs={12}>
            <MiPlanCard />
          </Grid>

          {/* Preparación de pedidos: con / sin escáner (preferencia de la cuenta) */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
              <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1, bgcolor: "grey.50", borderBottom: "1px solid #e0e0e0" }}>
                <QrCodeScannerIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Preparación de pedidos
                </Typography>
              </Box>
              <Box sx={{ px: 3, py: 2 }}>
                <FormControlLabel
                  control={<Switch checked={scanOnPrepare} onChange={handleScanToggle} />}
                  label="Procesar pedidos con escáner"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {scanOnPrepare
                    ? "Los operarios deben escanear cada producto (SKU/EAN) al preparar."
                    : "Los operarios marcan los productos manualmente (sin escáner) al preparar."}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Impresión de etiquetas: auto-imprimir al confirmar (preferencia de la cuenta) */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
              <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1, bgcolor: "grey.50", borderBottom: "1px solid #e0e0e0" }}>
                <PrintOutlinedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Impresión de etiquetas
                </Typography>
              </Box>
              <Box sx={{ px: 3, py: 2 }}>
                <FormControlLabel
                  control={<Switch checked={autoPrintOnConfirm} onChange={handleAutoPrintToggle} />}
                  label="Imprimir etiquetas automáticamente al confirmar"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {autoPrintOnConfirm
                    ? "Al confirmar el pedido en la packing station, se imprime la etiqueta de bulto en la impresora configurada abajo."
                    : "La etiqueta se imprime manualmente desde el listado de egresos."}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Impresora térmica de esta PC (config por dispositivo, vía QZ Tray) */}
          <Grid item xs={12}>
            <PrinterConfigCard />
          </Grid>

          {/* Seguridad: activar/desactivar 2FA */}
          <Grid item xs={12}>
            <SecurityCard
              onSnackbar={(severity, message) => {
                setAlertObj({ severity, message });
                setSnackbarOpen(true);
              }}
            />
          </Grid>

          {/* Perfil Card */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e0e0e0",
                overflow: "hidden",
              }}
            >
              {/* Card header */}
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "grey.50",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonOutlineIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Informacion personal
                  </Typography>
                </Box>
                {user?.is_owner && (
                  <Button
                    size="small"
                    variant={canEditUser ? "contained" : "outlined"}
                    startIcon={canEditUser ? <SaveOutlinedIcon fontSize="small" /> : <EditOutlinedIcon fontSize="small" />}
                    onClick={handleEditUser}
                    disabled={canEditUser && !hasUserChanges()}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                    }}
                  >
                    {canEditUser ? "Guardar" : "Editar"}
                  </Button>
                )}
              </Box>

              {/* Profile avatar + info */}
              <Box sx={{ px: 3, pt: 3, pb: 1, display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    fontSize: "1.2rem",
                    fontWeight: 700,
                    bgcolor: (theme) => theme.palette.primary.main,
                  }}
                >
                  {getInitials()}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {user?.firstname || ""} {user?.lastname || ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                    <EmailOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary">
                      {user?.email || ""}
                    </Typography>
                  </Box>
                  {user?.company && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                      <BusinessOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        {user.company}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mx: 3, my: 2 }} />

              {/* Form fields */}
              <Box sx={{ px: 3, pb: 3 }}>
                <Grid container spacing={2}>
                  {/* Usuario y Email son la identidad del login: solo lectura. */}
                  <Grid item xs={12}>
                    <TextField
                      label="Usuario"
                      size="small"
                      value={user?.name || ""}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      disabled
                      helperText="Es tu identificador de acceso, no se puede modificar"
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Nombre"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={user?.firstname || ""}
                      onChange={(e) => setUser({ ...user, firstname: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditUser}
                      error={Boolean(userErrors.firstname)}
                      helperText={userErrors.firstname}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Apellido"
                      size="small"
                      value={user?.lastname || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setUser({ ...user, lastname: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditUser}
                      error={Boolean(userErrors.lastname)}
                      helperText={userErrors.lastname}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Empresa"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={user?.company || ""}
                      onChange={(e) => setUser({ ...user, company: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditUser}
                      error={Boolean(userErrors.company)}
                      helperText={userErrors.company || "El nombre de tu negocio o marca"}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={user?.email || ""}
                      fullWidth
                      disabled
                      helperText="Es tu email de acceso, no se puede modificar"
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Depositos Card */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e0e0e0",
                overflow: "hidden",
              }}
            >
              {/* Card header */}
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "grey.50",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <WarehouseOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Deposito
                  </Typography>
                  {deposito?.name && (
                    <Chip
                      label={deposito.name}
                      size="small"
                      sx={{
                        fontSize: "0.7rem",
                        height: 22,
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
                {user?.is_owner && (
                  <Button
                    size="small"
                    variant={canEditDeposito ? "contained" : "outlined"}
                    startIcon={canEditDeposito ? <SaveOutlinedIcon fontSize="small" /> : <EditOutlinedIcon fontSize="small" />}
                    onClick={handleEditDeposito}
                    disabled={canEditDeposito && !hasDepositoChanges()}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                    }}
                  >
                    {canEditDeposito ? "Guardar" : "Editar"}
                  </Button>
                )}
              </Box>

              {/* Form fields */}
              <Box sx={{ px: 3, py: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Nombre del deposito"
                      size="small"
                      value={deposito?.name || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setDeposito({ ...deposito, name: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditDeposito}
                      error={Boolean(depositoErrors.name)}
                      helperText={depositoErrors.name}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Direccion
                    </Typography>
                  </Grid>

                  <Grid item xs={8}>
                    <TextField
                      label="Calle"
                      size="small"
                      value={deposito?.street || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setDeposito({ ...deposito, street: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditDeposito}
                      error={Boolean(depositoErrors.street)}
                      helperText={depositoErrors.street}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Numero"
                      size="small"
                      value={deposito?.number || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setDeposito({ ...deposito, number: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditDeposito}
                      error={Boolean(depositoErrors.number)}
                      helperText={depositoErrors.number}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Referencia"
                      size="small"
                      value={deposito?.reference || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setDeposito({ ...deposito, reference: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditDeposito}
                      error={Boolean(depositoErrors.reference)}
                      helperText={depositoErrors.reference}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Codigo postal"
                      size="small"
                      value={deposito?.postal_code || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setDeposito({ ...deposito, postal_code: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditDeposito}
                      error={Boolean(depositoErrors.postal_code)}
                      helperText={depositoErrors.postal_code}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Localidad"
                      size="small"
                      value={deposito?.department || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setDeposito({ ...deposito, department: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditDeposito}
                      error={Boolean(depositoErrors.department)}
                      helperText={depositoErrors.department}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Provincia"
                      size="small"
                      value={deposito?.province || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setDeposito({ ...deposito, province: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditDeposito}
                      error={Boolean(depositoErrors.province)}
                      helperText={depositoErrors.province}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Telefono"
                      size="small"
                      value={deposito?.phone || ""}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setDeposito({ ...deposito, phone: e.target.value })}
                      fullWidth
                      required
                      disabled={!canEditDeposito}
                      error={Boolean(depositoErrors.phone)}
                      helperText={depositoErrors.phone}
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={alertObj.severity}
          sx={{ width: "100%" }}
        >
          {alertObj.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Perfil;
